import {
    getSubscription,
    listSubscriptionsForCustomer
} from './queries';
import {
    activateCustomerSubscription,
    cancelCustomerSubscription,
    createSubscription,
    deleteSubscription,
    updateSubscription,
} from './mutations';

import {GraphQLQuery,} from '@aws-amplify/api';
import {
    CreateSubscription,
    DeleteSubscription,
    GetSubscription,
    ListSubscriptions,
    UpdateSubscription
} from "../types.ts";
import {notifyError} from "../../../../components/ServiceNotifications.tsx";
import {billingClient} from '../../billing-api-integration/client';
import {QueryClient} from '@tanstack/react-query';
import {Subscription} from '../types';

export const fetchRecords = async (query?: { limit?: number; nextToken?: string }) => {
    try {
        const response = await billingClient.graphql<GraphQLQuery<ListSubscriptions>>({
            query: listSubscriptionsForCustomer,
            variables: { limit: query?.limit, nextToken: query?.nextToken }
        });
        return {
            items: response.data?.listCustomerSubscriptionsForCustomer?.items,
            nextToken: response.data?.listCustomerSubscriptionsForCustomer?.nextToken,
        };
    } catch (e) {
        console.trace(e);
        notifyError(e);
    }
};

export const fetch = async (id: string) => {
    try {
        const response = await billingClient.graphql<GraphQLQuery<GetSubscription>>({
                                                                                 query: getSubscription,
                                                                                 variables: {id}
                                                                             });
        return response.data?.getCustomerSubscription;
    } catch (e) {
        console.trace(e);
        notifyError(e);
    }
};

export const add = async (payload: object) => {
    try {
        const response = await billingClient.graphql<GraphQLQuery<CreateSubscription>>({
                                                                                    query: createSubscription,
                                                                                    variables: {input: payload}
                                                                                });
        return response.data?.createCustomerSubscription;
    } catch (e) {
        console.trace(e);
        notifyError(e);
    }
};

// Alias for clarity
export { add as createSubscription };

export const activateSubscriptionRecord = async (id: string, queryClient?: QueryClient) => {
    try {
        const response = await billingClient.graphql<GraphQLQuery<{ activateCustomerSubscription: unknown }>>({
            query: activateCustomerSubscription,
            variables: { id }
        });
        if (queryClient) {
            queryClient.invalidateQueries({ queryKey: ['subscriptions'] });
        }
        return response.data?.activateCustomerSubscription;
    } catch (e) {
        console.trace(e);
        notifyError(e);
    }
};

export const cancelSubscriptionRecord = async (id: string, queryClient?: QueryClient) => {
    try {
        const response = await billingClient.graphql<GraphQLQuery<{ cancelCustomerSubscription: unknown }>>({
            query: cancelCustomerSubscription,
            variables: { id }
        });
        
        // TODO: Cancel in provider (Stripe/PayPal) if needed
        // This requires provider subscription ID mapping
        
        if (queryClient) {
            queryClient.invalidateQueries({ queryKey: ['subscriptions'] });
        }
        return response.data?.cancelCustomerSubscription;
    } catch (e) {
        console.trace(e);
        notifyError(e);
    }
};

export const update = async (
    id: string,
    record: Pick<Subscription, 'customerId' | 'status'> & { items?: Subscription['items'] },
) => {
    try {
        const input = {
            id,
            customerId: record.customerId,
            status: record.status,
            items: record.items || []
        };

        if (!input.customerId) {
            throw new Error('customerId is required for subscription update');
        }


        const response = await billingClient.graphql<GraphQLQuery<UpdateSubscription>>({
                                                                                    query: updateSubscription,
                                                                                    variables: {input}
                                                                                });
        return response.data?.updateCustomerSubscription;
    } catch (e) {
        console.trace(e);
        notifyError(e);
    }
};

export const deleteRecord = async (id: string) => {
    try {
        const response = await billingClient.graphql<GraphQLQuery<DeleteSubscription>>({
                                                                                    query: deleteSubscription,
                                                                                    variables: {input: {id}}
                                                                                });
        return response.data?.deleteCustomerSubscription;
    } catch (e) {
        console.trace(e);
        notifyError(e);
    }
};

export const subscribeToProduct = async (customerId: string, priceId: string, quantity = 1, queryClient?: QueryClient) => {
    const subscriptionPayload = {
        customerId,
        items: [{
            priceId,
            quantity
        }],
        status: 'Active'
    };

    const result = await add(subscriptionPayload);

    if (queryClient) {
        queryClient.invalidateQueries({queryKey: ['subscriptions']});
    }

    return result;
};

export const addProductToSubscription = async (subscriptionId: string, priceId: string, quantity = 1) => {
    try {
        // First fetch the current subscription
        const currentSubscription = await fetch(subscriptionId);
        if (!currentSubscription) {
            throw new Error('Subscription not found');
        }

        // Add the new product to existing items
        const newItem = {
            id: '',
            priceId,
            quantity,
            price: {
                product: {id: '', name: 'New Product'},
                amount: 0,
                billingPeriod: 'monthly'
            }
        };

        const updatedItems = {
            items: [
                ...currentSubscription.items.items,
                newItem
            ],
            nextToken: currentSubscription.items.nextToken
        };

        const updatedSubscription = {
            ...currentSubscription,
            items: updatedItems
        };

        return update(subscriptionId, updatedSubscription);
    } catch (e) {
        notifyError(e);
    }
};

export const removeProductFromSubscription = async (subscriptionId: string, itemId: string) => {
    try {
        const currentSubscription = await fetch(subscriptionId);
        if (!currentSubscription) {
            throw new Error('Subscription not found');
        }

        // Remove the item from subscription
        const updatedItems = {
            items: currentSubscription.items.items.filter(item => item.id !== itemId),
            nextToken: currentSubscription.items.nextToken
        };

        const updatedSubscription = {
            ...currentSubscription,
            items: updatedItems
        };

        return update(subscriptionId, updatedSubscription);
    } catch (e) {
        notifyError(e);
    }
};




